import React from "react";
import {
  Box,
  Button,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { FilterAltOff as ResetIcon } from "@mui/icons-material";
import type {
  CapabilityMapDataset,
  CapabilityMapFilters,
  CompanyTypeTag,
  DomainMode,
  SourceType,
  UseCaseTag,
} from "./types";

interface Props {
  dataset: CapabilityMapDataset;
  filters: CapabilityMapFilters;
  domainMode: DomainMode;
  searchQuery: string;
  onFiltersChange: (f: CapabilityMapFilters) => void;
  onDomainModeChange: (d: DomainMode) => void;
  onSearchChange: (q: string) => void;
  onReset: () => void;
}

const USE_CASE_TAGS: { value: UseCaseTag; label: string }[] = [
  { value: "isr", label: "ISR" },
  { value: "surveillance", label: "Surveillance" },
  { value: "logistics", label: "Logistics" },
  { value: "precision", label: "Precision" },
  { value: "inspection", label: "Inspection" },
  { value: "mapping", label: "Mapping" },
];

const COMPANY_TYPES: { value: CompanyTypeTag; label: string }[] = [
  { value: "oem", label: "OEM" },
  { value: "supplier", label: "Supplier" },
  { value: "software", label: "Software" },
  { value: "integrator", label: "Integrator" },
];

const SOURCE_TYPES: { value: SourceType; label: string }[] = [
  { value: "formal_tender", label: "Formal" },
  { value: "program_announcement", label: "Programme" },
  { value: "press_release", label: "Press" },
  { value: "patent", label: "Patent" },
];

const CapabilityMapFilterPanel: React.FC<Props> = ({
  dataset,
  filters,
  domainMode,
  searchQuery,
  onFiltersChange,
  onDomainModeChange,
  onSearchChange,
  onReset,
}) => {
  const toggleUseCaseTag = (tag: UseCaseTag) => {
    const next = filters.useCaseTags.includes(tag)
      ? filters.useCaseTags.filter((t) => t !== tag)
      : [...filters.useCaseTags, tag];
    onFiltersChange({ ...filters, useCaseTags: next });
  };

  const toggleCompanyType = (type: CompanyTypeTag) => {
    const next = filters.companyTypes.includes(type)
      ? filters.companyTypes.filter((t) => t !== type)
      : [...filters.companyTypes, type];
    onFiltersChange({ ...filters, companyTypes: next });
  };

  const toggleSourceType = (sourceType: SourceType) => {
    const next = filters.sourceTypes.includes(sourceType)
      ? filters.sourceTypes.filter((t) => t !== sourceType)
      : [...filters.sourceTypes, sourceType];
    onFiltersChange({ ...filters, sourceTypes: next });
  };

  const toggleSystemType = (stId: string) => {
    const next = filters.systemTypeIds.includes(stId)
      ? filters.systemTypeIds.filter((id) => id !== stId)
      : [...filters.systemTypeIds, stId];
    onFiltersChange({ ...filters, systemTypeIds: next });
  };

  const toggleComponentType = (ctId: string) => {
    const next = filters.componentTypeIds.includes(ctId)
      ? filters.componentTypeIds.filter((id) => id !== ctId)
      : [...filters.componentTypeIds, ctId];
    onFiltersChange({ ...filters, componentTypeIds: next });
  };

  const hasActiveFilters =
    filters.useCaseTags.length > 0 ||
    filters.companyTypes.length > 0 ||
    filters.sourceTypes.length > 0 ||
    filters.systemTypeIds.length > 0 ||
    filters.componentTypeIds.length > 0 ||
    domainMode !== "defense" ||
    searchQuery.length > 0;

  return (
    <Box
      sx={{
        width: 220,
        minWidth: 220,
        height: "100%",
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Capability Map
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search capabilities…"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          inputProps={{ style: { fontSize: 12 } }}
        />
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflow: "auto", px: 1.5, py: 1 }}>
        {/* Domain */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            display: "block",
            mb: 0.75,
          }}
        >
          Domain
        </Typography>
        <ToggleButtonGroup
          value={domainMode}
          exclusive
          size="small"
          onChange={(_e, v) => {
            if (v) onDomainModeChange(v);
          }}
          sx={{
            mb: 1.5,
            "& .MuiToggleButton-root": { px: 1, py: 0.25, fontSize: "0.7rem" },
          }}
        >
          <ToggleButton value="defense">Defense</ToggleButton>
          <ToggleButton value="industrial">Industrial</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>

        <Divider sx={{ mb: 1.5 }} />

        {/* Source Type */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            display: "block",
            mb: 0.75,
          }}
        >
          Source Type
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
          {SOURCE_TYPES.map(({ value, label }) => (
            <Tooltip key={value} title={`Filter to ${label} sources`}>
              <ToggleButton
                value={value}
                size="small"
                selected={filters.sourceTypes.includes(value)}
                onChange={() => toggleSourceType(value)}
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize: "0.7rem",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {label}
              </ToggleButton>
            </Tooltip>
          ))}
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* System Type */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            display: "block",
            mb: 0.75,
          }}
        >
          System Type
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
          {dataset.systemTypes.map((st) => (
            <Tooltip key={st.id} title={st.description}>
              <ToggleButton
                value={st.id}
                size="small"
                selected={filters.systemTypeIds.includes(st.id)}
                onChange={() => toggleSystemType(st.id)}
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize: "0.65rem",
                  border: "1px solid",
                  borderColor: "divider",
                  textTransform: "none",
                }}
              >
                {st.label}
              </ToggleButton>
            </Tooltip>
          ))}
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Component Type */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            display: "block",
            mb: 0.75,
          }}
        >
          Component Type
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
          {dataset.componentTypes.map((ct) => (
            <Tooltip key={ct.id} title={ct.description}>
              <ToggleButton
                value={ct.id}
                size="small"
                selected={filters.componentTypeIds.includes(ct.id)}
                onChange={() => toggleComponentType(ct.id)}
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize: "0.65rem",
                  border: "1px solid",
                  borderColor: "divider",
                  textTransform: "none",
                }}
              >
                {ct.label}
              </ToggleButton>
            </Tooltip>
          ))}
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Use Case Type */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            display: "block",
            mb: 0.75,
          }}
        >
          Use Case Type
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
          {USE_CASE_TAGS.map(({ value, label }) => (
            <Tooltip key={value} title={`Filter to ${label} use cases`}>
              <ToggleButton
                value={value}
                size="small"
                selected={filters.useCaseTags.includes(value)}
                onChange={() => toggleUseCaseTag(value)}
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize: "0.7rem",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {label}
              </ToggleButton>
            </Tooltip>
          ))}
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Company Type */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            display: "block",
            mb: 0.75,
          }}
        >
          Company Type
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {COMPANY_TYPES.map(({ value, label }) => (
            <Tooltip key={value} title={`Show only ${label} companies`}>
              <ToggleButton
                value={value}
                size="small"
                selected={filters.companyTypes.includes(value)}
                onChange={() => toggleCompanyType(value)}
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize: "0.7rem",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {label}
              </ToggleButton>
            </Tooltip>
          ))}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 1.5, py: 1 }}>
        <Button
          size="small"
          fullWidth
          startIcon={<ResetIcon fontSize="small" />}
          onClick={onReset}
          disabled={!hasActiveFilters}
          sx={{ fontSize: "0.75rem" }}
        >
          Reset Filters
        </Button>
      </Box>
    </Box>
  );
};

export default CapabilityMapFilterPanel;
